import {
  ActionIcon,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconHelpCircle, IconPlus } from '@tabler/icons-react';
import { ModalFooter } from 'components/ModalFooter';
import { i18nKeys, useTranslation } from 'locales';
import { AdditionalAssigneesSelect, TaskTypeForm } from 'modules/Tasks';
import type { RecoveryPlanStep } from 'types/recovery-plan';

import { useRecoveryTaskStepForm } from './useRecoveryTaskStepForm';

type RecoveryTaskStepFormProps = {
  step?: RecoveryPlanStep;
  onSubmit: (step: RecoveryPlanStep) => void;
};

export const RecoveryTaskStepForm = ({ step, onSubmit }: RecoveryTaskStepFormProps) => {
  const { t } = useTranslation();
  const [isTaskTypeModalOpen, taskTypeModal] = useDisclosure(false);
  const {
    form,
    taskTypeOptions,
    assigneeOptions,
    dueDateOptions,
    mandatoryOptions,
    users,
    submit,
    createTaskType,
  } = useRecoveryTaskStepForm({ step, onSubmit });

  return (
    <>
      <SimpleGrid cols={2} p="md">
        <TextInput
          label={t(i18nKeys.TASK.ATTRIBUTES.TITLE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.TITLE_PLACEHOLDER)}
          required
          autoFocus
          {...form.getInputProps('title')}
        />
        <Group align="end" gap={6} wrap="nowrap">
          <Select
            label={t(i18nKeys.TASK.ATTRIBUTES.TASK_TYPE)}
            placeholder={t(i18nKeys.TASK.ATTRIBUTES.TASK_TYPE_PLACEHOLDER)}
            data={taskTypeOptions}
            required
            searchable
            flex={1}
            {...form.getInputProps('taskTypeId')}
          />
          <ActionIcon
            variant="subtle"
            color="purple"
            mb={4}
            aria-label={t(i18nKeys.ADD_TASK_TYPE)}
            onClick={taskTypeModal.open}
          >
            <IconPlus size={18} />
          </ActionIcon>
        </Group>
        <Select
          label={t(i18nKeys.TASK.ATTRIBUTES.ASSIGNEE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.ASSIGNEE_PLACEHOLDER)}
          data={assigneeOptions}
          required
          searchable
          {...form.getInputProps('userId')}
        />
        <AdditionalAssigneesSelect
          users={users}
          primaryUserId={form.values.userId}
          {...form.getInputProps('additionalAssigneeIds')}
        />
        <NumberInput
          label={t(i18nKeys.DELAY_WITH_PREVIOUS_ACTION)}
          required
          min={1}
          allowNegative={false}
          allowDecimal={false}
          {...form.getInputProps('delay')}
        />
        <Select
          label={t(i18nKeys.DUE_DATE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.DUE_DATE_PLACEHOLDER)}
          data={dueDateOptions}
          required
          {...form.getInputProps('dueDateDelay')}
        />
        <Select
          label={t(i18nKeys.TASK.ATTRIBUTES.MANDATORY)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.MANDATORY_PLACEHOLDER)}
          data={mandatoryOptions}
          required
          {...form.getInputProps('mandatory')}
        />
        <Textarea
          label={t(i18nKeys.DESCRIPTION)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.DESCRIPTION_PLACEHOLDER)}
          style={{ gridColumn: '1 / -1' }}
          {...form.getInputProps('description')}
        />
        <Group justify="space-between" align="center" style={{ gridColumn: '1 / -1' }}>
          <Checkbox
            label={t(i18nKeys.TASK.ATTRIBUTES.ADD_START_DATE)}
            {...form.getInputProps('isAddStartDateChecked', {
              type: 'checkbox',
            })}
          />
          <Tooltip
            multiline
            w={250}
            withArrow
            label={t(i18nKeys.TASK.ATTRIBUTES.START_DATE_TOOLTIP)}
            position="top"
          >
            <IconHelpCircle color="grey" />
          </Tooltip>
        </Group>
        {form.values.isAddStartDateChecked && (
          <Group style={{ gridColumn: '1 / -1' }}>
            <NumberInput
              w={70}
              placeholder="1"
              min={1}
              allowNegative={false}
              allowDecimal={false}
              {...form.getInputProps('startOffsetDays')}
            />
            <Text>{t(i18nKeys.TASK.ATTRIBUTES.DAYS_BEFORE_DUE_DATE)}</Text>
          </Group>
        )}
      </SimpleGrid>
      <ModalFooter onSave={submit} isDisabled={!form.isValid()} />
      <Modal
        opened={isTaskTypeModalOpen}
        onClose={taskTypeModal.close}
        title={t(i18nKeys.ADD_TASK_TYPE)}
        zIndex={1000}
      >
        <TaskTypeForm
          onSubmit={data =>
            createTaskType(data, {
              onSuccess: newTaskType => {
                form.setFieldValue('taskTypeId', newTaskType.id.toString());
                taskTypeModal.close();
              },
            })
          }
          onCancel={taskTypeModal.close}
        />
      </Modal>
    </>
  );
};
